import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import {
  getDitaContent,
  selectDitaContent,
} from "../../../components/Content/ContentSlice";
import DateRangeFilter from "../../../components/Updates/DateRangeFilter";
import {
  applyFiltersToSearchParams,
  cardMatchesFilters,
  parseFiltersFromSearchParams,
  RANGE_KEYS,
  readCardDate,
} from "../../../components/Updates/updatesFilterUtils";
import filterStyles from "../../../components/Updates/UpdatesFilters.module.scss";
import GuidelineDateSection from "./GuidelineDateSection";
import {
  extractGuidelineNameFromPath,
  isValidGuidelineName,
  processChaptersToUpdates,
} from "./guidelineUpdatesUtils";
import GuidelineChip from "../../../components/Guideline/GuidelineChip";
import { getGuidelineUpdatesDitamapPath } from "../../../utils/guidelineAbbreviations";
import { mpstopicfile } from "../../../utils/guidelines";
import useAppModernisation from "../../../hooks/useAppModernisation";
import useDebouncedMutationEffect from "../../../hooks/useDebouncedMutationEffect";
import { useUpdatesClickAction } from "../../../hooks/updatesHooks/useUpdatesClickAction";
import styles from "./GuidelineUpdatesPage.module.scss";

const DateRangePopup = lazy(() =>
  import("../../../components/Updates/DateRangePopup"),
);

const GuidelineUpdatesPage = () => {
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();
  const location = useLocation();
  const ref = useRef(null);
  const { clickAction } = useUpdatesClickAction(ref);
  const [guidelineName, setGuidelineName] = useState("");
  const [guidelineInfo, setGuidelineInfo] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCustomRange, setShowCustomRange] = useState(false);
  const { isAppMode } = useAppModernisation();

  const filters = parseFiltersFromSearchParams(searchParams);

  useDebouncedMutationEffect(ref, clickAction, [], 100);

  useEffect(() => {
    const extractedName = extractGuidelineNameFromPath(location.pathname);

    if (isValidGuidelineName(extractedName)) {
      setGuidelineName(extractedName);
      setGuidelineInfo(mpstopicfile[extractedName]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!guidelineName || !content) return;

    const ditamapPath = getGuidelineUpdatesDitamapPath(guidelineName);
    if (!ditamapPath) {
      console.warn(`No updates ditamap found for guideline: ${guidelineName}`);
      return;
    }

    const updateContent = getDitaContent(ditamapPath, content, dispatch);
    if (!updateContent) {
      console.warn(`Could not load updates for guideline: ${guidelineName}`);
      return;
    }

    const chapters = updateContent.getElementsByTagName("chapter");
    setUpdates(processChaptersToUpdates(chapters));
  }, [guidelineName, content, dispatch]);

  // Decorate each topicref with its <created> date by fetching the topic dita,
  const decoratedChapters = useMemo(() => {
    return updates.map((chapter) => ({
      ...chapter,
      updates: chapter.updates.map((topicref) => {
        const href = topicref.getAttribute("href");
        const topicDoc = href
          ? getDitaContent("Updates/" + href, content, dispatch)
          : null;
        return {
          topicref,
          date: topicDoc ? readCardDate(topicDoc) : null,
        };
      }),
    }));
  }, [updates, content, dispatch]);
//final result with filter and remove empty chapters
  const visibleChapters = useMemo(() => {
    return decoratedChapters
      .map((chapter) => ({
        ...chapter,
        updates: chapter.updates.filter((u) =>
          cardMatchesFilters(
            { date: u.date, guidelineCode: null },
            { ...filters, guidelines: [] },
          ),
        ),
      }))
      .filter((chapter) => chapter.updates.length > 0);
  }, [decoratedChapters, filters]);

  const handleFiltersChange = (next) => {
    setSearchParams(
      applyFiltersToSearchParams(searchParams, { ...next, guidelines: [] }),
      { replace: true },
    );
  };

  const handleOpenCustomRange = () => setShowCustomRange(true);

  const handleApplyCustomRange = ({ from, to }) => {
    setShowCustomRange(false);
    handleFiltersChange({
      ...filters,
      range: RANGE_KEYS.CUSTOM,
      from,
      to,
    });
  };

  const clearAll = () => {
    handleFiltersChange({
      range: RANGE_KEYS.ALL,
      from: null,
      to: null,
      guidelines: [],
    });
  };

  const earliestUpdateDate = useMemo(() => {
    const dates = decoratedChapters
      .flatMap((c) => c.updates.map((u) => u.date))
      .filter(Boolean);
    if (dates.length === 0) return null;
    return new Date(Math.min(...dates.map((d) => d.getTime())));
  }, [decoratedChapters]);

  const hasActiveFilters = filters.range !== RANGE_KEYS.ALL;

  return (
    <div className={classNames("size-16 regular", styles.updatePage)} ref={ref}>
      {!isAppMode && guidelineInfo && (
        <div className={classNames(styles.banner, styles[guidelineName])}>
          <div className={styles.bannerContent}>
            <h1 className={classNames("size-24 semibold", styles.bannerTitle)}>
              Practice-changing updates
            </h1>
            <div className={styles.guidelineBadge}>
              <GuidelineChip
                iconClassSuffix={guidelineInfo.squareIconClass}
                linkText={guidelineInfo.text}
                onClickProps={guidelineInfo.onClickProps}
                showArrowIcon={false}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.filterBar}>
        <span
          className={classNames("size-14 regular", styles.filterLabel)}
        >
          Filter by:
        </span>
        <DateRangeFilter
          filters={filters}
          onChange={handleFiltersChange}
          onOpenCustomRange={handleOpenCustomRange}
        />
        {hasActiveFilters ? (
          <button
            type="button"
            className={filterStyles.clearButton}
            aria-label="Clear filters"
            onClick={clearAll}
          >
            <i className="tg-icons-Cross"></i>
            <span>Clear Filters</span>
          </button>
        ) : null}
      </div>

      {visibleChapters.length === 0 && (
        <div className={styles.noUpdates}>
          <p>No updates available for this guideline yet.</p>
        </div>
      )}

      <div className={styles.contentSection}>
        {visibleChapters.map((chapter, chapterIndex) => (
          <GuidelineDateSection
            key={chapterIndex}
            dateHref={chapter.dateHref}
            dateTitle={chapter.dateTitle}
            updates={chapter.updates.map((u) => u.topicref)}
          />
        ))}
      </div>

      {showCustomRange ? (
        <Suspense fallback={null}>
          <DateRangePopup
            initialFrom={filters.from}
            initialTo={filters.to}
            earliestDate={earliestUpdateDate}
            onApply={handleApplyCustomRange}
            onClose={() => setShowCustomRange(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

export default GuidelineUpdatesPage;
